import React, { useState, useEffect } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { getISTDateParts } from "../utils/dateTime";

import {
    BarChart,
    Bar,
    Cell,
    ComposedChart,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from "recharts";

const getDayOfWeek = (date) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(date));

    if (!match) {
        return null;
    }

    const [, year, month, day] = match;
    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))).getUTCDay();
};

const getMonthIndex = (date) => {
    const match = /^(\d{4})-(\d{2})/.exec(String(date));
    if (!match) return null;

    return Number(match[1]) * 12 + Number(match[2]) - 1;
};

const getMonthlySales = (dailySales, fromDate, toDate) => {
    const startMonth = getMonthIndex(fromDate);
    const endMonth = getMonthIndex(toDate);
    const monthlyTotals = new Map();

    if (startMonth !== null && endMonth !== null && endMonth >= startMonth) {
        for (let month = startMonth; month <= endMonth; month += 1) {
            monthlyTotals.set(month, 0);
        }
    }

    dailySales.forEach(({ date, sale }) => {
        const month = getMonthIndex(date);
        if (month !== null) {
            monthlyTotals.set(month, (monthlyTotals.get(month) || 0) + Number(sale || 0));
        }
    });

    let previousSale = null;

    return [...monthlyTotals.entries()]
        .sort(([firstMonth], [secondMonth]) => firstMonth - secondMonth)
        .map(([month, sale]) => {
            const year = Math.floor(month / 12);
            const monthOfYear = month % 12;
            const growthPercent = previousSale > 0
                ? ((sale - previousSale) / previousSale) * 100
                : null;
            previousSale = sale;

            return {
                month,
                label: new Intl.DateTimeFormat("en-IN", {
                    month: "short",
                    year: "numeric",
                    timeZone: "UTC"
                }).format(new Date(Date.UTC(year, monthOfYear, 1))),
                sale,
                growthPercent
            };
        });
};

function SalesReportModal({ show, onClose }) {

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [gstRateFilter, setGstRateFilter] = useState("");

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("");

    const [report, setReport] = useState(null);

    const [dailySales, setDailySales] = useState([]);

    const [showDetailed, setShowDetailed] = useState(false);
    const [chartType, setChartType] = useState("bar");
    const [chartPeriod, setChartPeriod] = useState("daily");


    useEffect(() => {

        if (show) {

            const { year, month, day } = getISTDateParts();
            const from = `${year}-${month}-01`;
            const to = `${year}-${month}-${day}`;

            setFromDate(from);
            setToDate(to);
            setGstRateFilter("");

            fetchSales(from, to);
        }

    }, [show]);

    useEffect(() => {

        if (fromDate && toDate) {

            fetchSales(fromDate, toDate);

            fetchDetailedReport(fromDate, toDate);
        }

    }, [fromDate, toDate]);

    useEffect(() => {
        if (fromDate && toDate) {
            fetchSales(fromDate, toDate);
        }
    }, [gstRateFilter]);

    const fetchSales = async (
        customFrom = fromDate,
        customTo = toDate
    ) => {

        if (!customFrom || !customTo) {
            return;
        }

        try {

            setLoading(true);
            setMessage("");
            setMessageType("");

            const token = localStorage.getItem("token");
            const gstQuery = gstRateFilter !== "" ? `&gstRate=${encodeURIComponent(gstRateFilter)}` : "";

            const res = await fetch(
                `/v1/bills/sales/total?from=${customFrom}&to=${customTo}${gstQuery}`,
                {
                    headers: {
                        Authorization: "Bearer " + token
                    }
                }
            );

            if (!res.ok) {
                throw new Error("Failed");
            }

            const data = await res.json();

            setReport(data);

        } catch (err) {

            console.error(err);
            setMessage("Failed to fetch sales report");
            setMessageType("danger");

        } finally {

            setLoading(false);
        }
    };

    const fetchDetailedReport = async (
        customFrom = fromDate,
        customTo = toDate
    ) => {

        try {

            setLoading(true);

            const token = localStorage.getItem("token");

            const res = await fetch(
                `/v1/bills/sales/daily?from=${customFrom}&to=${customTo}`,
                {
                    headers: {
                        Authorization: "Bearer " + token
                    }
                }
            );

            if (!res.ok) {
                throw new Error("Failed");
            }

            const data = await res.json();

            setDailySales(data);

            setShowDetailed(true);

        } catch (err) {

            console.error(err);
            setMessage("Failed to fetch detailed report");
            setMessageType("danger");

        } finally {

            setLoading(false);
        }
    };

    if (!show) return null;

    const monthlySales = getMonthlySales(dailySales, fromDate, toDate);

    const downloadPDF = async () => {

        try {

            const pdf = new jsPDF("p", "mm", "a4");

            pdf.setFontSize(18);

            pdf.text("Sales Report", 14, 20);

            pdf.setFontSize(11);

            pdf.text(`From: ${fromDate}`, 14, 30);
            pdf.text(`To: ${toDate}`, 80, 30);
            if (gstRateFilter !== "") {
                pdf.text(`GST: ${gstRateFilter}%`, 14, 36);
            }

            // ✅ Summary Table
            autoTable(pdf, {
                startY: 40,
                head: [["Metric", "Value"]],
                body: [
                    ["Total Bills", report?.totalBills || 0],
                    ["Gross Sale", `Rs. ${report?.grossSale || 0}`],
                    ["Total Taxable Sale", `Rs. ${report?.totalSale || 0}`],
                    ["Total GST", `Rs. ${report?.totalGst || 0}`],
                    ["Total Discount", `Rs. ${report?.totalDiscount || 0}`]
                ]
            });

            // ✅ Capture chart
            const chartElement = document.getElementById("sales-chart");

            if (chartElement) {

                const canvas = await html2canvas(chartElement);

                const imgData = canvas.toDataURL("image/png");

                pdf.addPage();

                pdf.setFontSize(16);

                pdf.text(
                    chartPeriod === "monthly" ? "Monthly Earnings & Growth" : "Daily Earnings Graph",
                    14,
                    20
                );

                pdf.addImage(
                    imgData,
                    "PNG",
                    10,
                    30,
                    180,
                    90
                );
            }

            autoTable(pdf, {
                startY: 140,
                head: chartPeriod === "monthly"
                    ? [["Month", "Earnings", "Growth vs. Previous Month"]]
                    : [["Date", "Sale"]],
                body: chartPeriod === "monthly"
                    ? monthlySales.map(({ label, sale, growthPercent }) => [
                        label,
                        `Rs. ${sale}`,
                        Number.isFinite(growthPercent) ? `${growthPercent.toFixed(1)}%` : "-"
                    ])
                    : dailySales.map((d) => [
                        d.date,
                        `Rs. ${d.sale}`
                    ])
            });

            pdf.save(
                `sales-report-${fromDate}-to-${toDate}.pdf`
            );

        } catch (err) {

            console.error(err);

            setMessage("Failed to download PDF");
            setMessageType("danger");
        }
    };

    return (
        <>
            <div className="modal-backdrop fade show"></div>

            <div className="modal show d-block">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">

                        <div className="modal-header">
                            <h5>Total Sales Report</h5>

                            <button
                                className="btn-close"
                                onClick={onClose}
                            ></button>
                        </div>

                        <div className="modal-body">
                            {loading && (
                                <div className="modal-loading-overlay">
                                    <div className="text-center">
                                        <div className="spinner-border" role="status"></div>
                                        <p className="mt-2 mb-0">Loading report...</p>
                                    </div>
                                </div>
                            )}

                            {message && (
                                <div className={`alert alert-${messageType || "info"} py-2`}>
                                    {message}
                                </div>
                            )}

                            <div className="row">

                                <div className="col-md-6 mb-3">
                                    <label>From Date</label>

                                    <input
                                        type="date"
                                        className="form-control"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label>To Date</label>

                                    <input
                                        type="date"
                                        className="form-control"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label>GST Filter</label>

                                    <select
                                        className="form-control"
                                        value={gstRateFilter}
                                        onChange={(e) => setGstRateFilter(e.target.value)}
                                    >
                                        <option value="">All GST Rates</option>
                                        <option value="0">0%</option>
                                        <option value="5">5%</option>
                                        <option value="9">9%</option>
                                        <option value="12">12%</option>
                                        <option value="18">18%</option>
                                    </select>
                                </div>

                            </div>

                            {report && (
                                <div className="mt-4 border rounded p-3 bg-light">

                                    <div className="mt-3">
                                        <button
                                            className="btn btn-success w-100"
                                            onClick={downloadPDF}
                                        >
                                            Download PDF Report
                                        </button>
                                    </div>
                                    <h6 className="mb-3">
                                        Sales Summary
                                    </h6>

                                    {gstRateFilter !== "" && (
                                        <div className="alert alert-info py-2 mb-3">
                                            Showing totals for GST rate {gstRateFilter}% only.
                                        </div>
                                    )}

                                    <div className="d-flex justify-content-between">
                                        <span className="text-danger fw-semibold">Gross Sale</span>
                                        <strong className="text-danger">₹{report.grossSale}</strong>
                                    </div>

                                    <hr />

                                    <div className="d-flex justify-content-between">
                                        <span>Total Bills</span>
                                        <strong>{report.totalBills}</strong>
                                    </div>

                                    <hr />

                                    <div className="d-flex justify-content-between">
                                        <span>Total Taxable Sale</span>
                                        <strong>₹{report.totalSale}</strong>
                                    </div>

                                    <hr />

                                    <div className="d-flex justify-content-between">
                                        <span>Total GST</span>
                                        <strong>₹{report.totalGst}</strong>
                                    </div>

                                    <hr />

                                    <div className="d-flex justify-content-between">
                                        <span>Total Discount</span>
                                        <strong>₹{report.totalDiscount}</strong>
                                    </div>

                                </div>
                            )}

                            {showDetailed && (
                                <div className="mt-4 border rounded p-3">

                                    <h5 className="mb-3">
                                        {chartPeriod === "monthly"
                                            ? "Monthly Earnings & Growth"
                                            : "Daily Earnings Graph"}
                                    </h5>

                                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                                        <div className="btn-group" role="group" aria-label="Chart period">
                                            <button
                                                type="button"
                                                className={`btn ${chartPeriod === "daily" ? "btn-primary" : "btn-outline-primary"}`}
                                                aria-pressed={chartPeriod === "daily"}
                                                onClick={() => setChartPeriod("daily")}
                                            >
                                                Daily
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn ${chartPeriod === "monthly" ? "btn-primary" : "btn-outline-primary"}`}
                                                aria-pressed={chartPeriod === "monthly"}
                                                onClick={() => setChartPeriod("monthly")}
                                            >
                                                Monthly Growth
                                            </button>
                                        </div>

                                        {chartPeriod === "daily" && (
                                            <div className="btn-group" role="group" aria-label="Chart type">
                                                <button
                                                    type="button"
                                                    className={`btn ${chartType === "bar" ? "btn-primary" : "btn-outline-primary"}`}
                                                    aria-pressed={chartType === "bar"}
                                                    onClick={() => setChartType("bar")}
                                                >
                                                    Bar
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn ${chartType === "line" ? "btn-primary" : "btn-outline-primary"}`}
                                                    aria-pressed={chartType === "line"}
                                                    onClick={() => setChartType("line")}
                                                >
                                                    Line
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {chartPeriod === "monthly" && (
                                        <p className="small text-muted mb-2">
                                            Monthly earnings and change from the previous month for the selected date range.
                                            Select a range covering multiple months to see growth.
                                        </p>
                                    )}

                                    <div
                                        id="sales-chart"
                                        style={{ width: "100%", height: 350 }}
                                    >

                                        <ResponsiveContainer>

                                            {chartPeriod === "monthly" ? (
                                                <ComposedChart data={monthlySales}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="label" />
                                                    <YAxis yAxisId="earnings" />
                                                    <YAxis
                                                        yAxisId="growth"
                                                        orientation="right"
                                                        tickFormatter={(value) => `${value}%`}
                                                    />
                                                    <Tooltip
                                                        formatter={(value, name) => (
                                                            name === "growthPercent"
                                                                ? [`${Number(value).toFixed(1)}%`, "Month-over-month growth"]
                                                                : [`₹${Number(value).toLocaleString("en-IN")}`, "Monthly earnings"]
                                                        )}
                                                    />
                                                    <Bar
                                                        yAxisId="earnings"
                                                        dataKey="sale"
                                                        name="sale"
                                                        fill="#0d6efd"
                                                    />
                                                    <Line
                                                        yAxisId="growth"
                                                        type="monotone"
                                                        dataKey="growthPercent"
                                                        name="growthPercent"
                                                        stroke="#198754"
                                                        strokeWidth={2}
                                                        connectNulls={false}
                                                        dot
                                                    />
                                                </ComposedChart>
                                            ) : chartType === "bar" ? (
                                                <BarChart data={dailySales}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="date" />
                                                    <YAxis />
                                                    <Tooltip />
                                                    <Bar dataKey="sale" fill="#0d6efd">
                                                        {dailySales.map((entry, index) => {
                                                            const dayOfWeek = getDayOfWeek(entry.date);
                                                            const fill = dayOfWeek === 0 || dayOfWeek === 5 || dayOfWeek === 6
                                                                ? "#198754"
                                                                : Number(entry.sale) < 2000
                                                                    ? "#ffc107"
                                                                    : "#0d6efd";

                                                            return (
                                                                <Cell
                                                                    key={`sale-${entry.date}-${index}`}
                                                                    fill={fill}
                                                                />
                                                            );
                                                        })}
                                                    </Bar>
                                                </BarChart>
                                            ) : (
                                                <LineChart data={dailySales}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="date" />
                                                    <YAxis />
                                                    <Tooltip />
                                                    <Line
                                                        type="monotone"
                                                        dataKey="sale"
                                                        stroke="#0d6efd"
                                                        strokeWidth={2}
                                                        dot
                                                    />
                                                </LineChart>
                                            )}

                                        </ResponsiveContainer>

                                    </div>

                                </div>
                            )}

                        </div>

                    </div>
                </div>
            </div>
        </>
    );
}

export default SalesReportModal;
